import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { downloadAssignment, downloadStudentSubmission, gradeStudentSubmission } from "../paperAssignmentSlice";
import { formatScore, roundScoreValue } from "../../../utils/textUtils";

const HomeworkFilesModal = ({ isOpen, onClose, homeworkFiles, studentName, className, onUpdate }) => {
  const dispatch = useDispatch();
  const [expandedSubmissions, setExpandedSubmissions] = useState(new Set());
  const [gradingData, setGradingData] = useState({});
  const [uploadingFeedback, setUploadingFeedback] = useState(new Set());

  const handleDownload = async (s3Key, fileName) => {
    if (!s3Key) {
      alert("파일을 다운로드할 수 없습니다.");
      return;
    }

    try {
      const result = await dispatch(downloadAssignment(s3Key));
      
      if (downloadAssignment.fulfilled.match(result)) {
        const { download_url, filename } = result.payload;
        // 새 탭에서 PDF 열기
        window.open(download_url, '_blank');
      }
    } catch (error) {
      console.error('Error downloading file:', error);
      alert("파일 다운로드 중 오류가 발생했습니다.");
    }
  };

  const handleDownloadSubmission = async (s3Key, fileName) => {
    try {
      const result = await dispatch(downloadStudentSubmission(s3Key));
      
      if (downloadStudentSubmission.fulfilled.match(result)) {
        const { download_url, filename } = result.payload;
        // 새 탭에서 PDF 열기  
        window.open(download_url, '_blank');
      }
    } catch (error) {
      console.error('Error downloading submission:', error);
      alert("제출물 다운로드 중 오류가 발생했습니다.");
    }
  };

  const handleDownloadFeedback = async (s3Key, fileName) => {
    try {
      const result = await dispatch(downloadAssignment(s3Key));
      
      if (downloadAssignment.fulfilled.match(result)) {
        const { download_url, filename } = result.payload;
        // 새 탭에서 첨삭 PDF 열기
        window.open(download_url, '_blank');
      }
    } catch (error) {
      console.error('Error downloading feedback:', error);
      alert("첨삭 파일 다운로드 중 오류가 발생했습니다.");
    }
  };

  const toggleSubmissionExpansion = (submissionId) => {
    const newExpanded = new Set(expandedSubmissions);
    if (newExpanded.has(submissionId)) {
      newExpanded.delete(submissionId);
    } else {
      newExpanded.add(submissionId);
    }
    setExpandedSubmissions(newExpanded);
  };

  const updateGradingData = (submissionId, field, value) => {
    setGradingData(prev => ({
      ...prev,
      [submissionId]: {
        ...prev[submissionId],
        [field]: value
      }
    }));
  };

  const clearFeedbackFile = (submissionId) => {
    setGradingData(prev => ({
      ...prev,
      [submissionId]: {
        ...prev[submissionId],
        feedbackFile: null,
        deleteFeedback: true
      }
    }));
  };

  const handleSubmitGrade = async (submissionId) => {
    const data = gradingData[submissionId];
    if (!data || data.score === undefined || data.score === '') {
      alert('점수를 입력해주세요.');
      return;
    }

    const score = roundScoreValue(data.score);
    if (score === null) {
      alert('점수는 숫자로 입력해주세요.');
      return;
    }

    if (score < 0 || score > 100) {
      alert('점수는 0-100 사이여야 합니다.');
      return;
    }

    setUploadingFeedback(prev => new Set([...prev, submissionId]));

    try {
      const formData = new FormData();
      formData.append('submission_id', submissionId);
      formData.append('score', score.toString());
      
      if (data.feedbackFile) {
        formData.append('feedback_file', data.feedbackFile);
      }
      
      if (data.deleteFeedback) {
        formData.append('delete_feedback', 'true');
      }

      const result = await dispatch(gradeStudentSubmission(formData));
      
      if (gradeStudentSubmission.fulfilled.match(result)) {
        alert('채점이 완료되었습니다!');
        setGradingData(prev => ({ ...prev, [submissionId]: {} }));
        onUpdate && onUpdate(); // 데이터 새로고침
      }
    } catch (error) {
      console.error('Error submitting grade:', error);
      alert('채점 제출 중 오류가 발생했습니다.');
    } finally {
      setUploadingFeedback(prev => {
        const newSet = new Set(prev);
        newSet.delete(submissionId);
        return newSet;
      });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: "rgba(0, 0, 0, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1000,
        padding: "20px"
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: "white",
          borderRadius: "16px",
          width: "100%",
          maxWidth: "800px",
          maxHeight: "80vh",
          overflowY: "auto", WebkitOverflowScrolling: "touch",
          boxShadow: "0 20px 60px rgba(0, 0, 0, 0.15)"
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: "32px 32px 24px 32px",
          borderBottom: "1px solid #f0f0f0"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
            <div>
              <h2 style={{
                margin: "0 0 8px 0",
                fontSize: "24px",
                color: "#1a1a1a",
                fontWeight: "700"
              }}>
                과제 목록
              </h2>
              <p style={{
                margin: "0",
                fontSize: "16px",
                color: "#7A8490",
                fontWeight: "500"
              }}>
                {studentName} • {className}
              </p>
            </div>
            <button
              onClick={onClose}
              style={{
                background: "none",
                border: "none",
                fontSize: "28px",
                cursor: "pointer",
                color: "#B9C1C9",
                padding: "8px",
                borderRadius: "50%",
                width: "44px",
                height: "44px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "all 0.2s"
              }}
              onMouseOver={(e) => {
                e.target.style.background = "#F4F8F7";
                e.target.style.color = "#7A8490";
              }}
              onMouseOut={(e) => {
                e.target.style.background = "none";
                e.target.style.color = "#B9C1C9";
              }}
            >
              ×
            </button>
          </div>
        </div>

        {/* Modal Content */}
        <div style={{ padding: "32px" }}>
          {homeworkFiles.length === 0 ? (
            <div style={{
              textAlign: "center",
              padding: "60px 20px",
              color: "#9ca3af"
            }}>
              <div style={{
                fontSize: "48px",
                marginBottom: "16px",
                opacity: "0.5"
              }}>
                
              </div>
              <p style={{
                margin: "0",
                fontSize: "16px",
                fontWeight: "500"
              }}>
                등록된 과제 파일이 없습니다.
              </p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
              {homeworkFiles.map((file, index) => (
                <div key={file.id} style={{
                  background: "#f0f9ff",
                  padding: "20px",
                  borderRadius: "12px",
                  border: "1px solid #bae6fd"
                }}>
                  {/* 과제 헤더 */}
                  <div style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "20px"
                  }}>
                    <span style={{ fontSize: "20px" }}></span>
                    <h3 style={{
                      margin: "0",
                      fontSize: "18px",
                      color: "#3A434E",
                      fontWeight: "600",
                      flex: 1
                    }}>
                      {file.title}
                    </h3>
                    <button
                      onClick={() => handleDownload(file.s3_key, file.file_name)}
                      disabled={!file.s3_key}
                      style={{
                        padding: "8px 16px",
                        backgroundColor: file.s3_key ? "#3b82f6" : "#B9C1C9",
                        color: "white",
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "13px",
                        fontWeight: "600",
                        cursor: file.s3_key ? "pointer" : "not-allowed",
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        transition: "all 0.2s"
                      }}
                      onMouseOver={(e) => {
                        if (file.s3_key) e.currentTarget.style.backgroundColor = "#2563eb";
                      }}
                      onMouseOut={(e) => {
                        if (file.s3_key) e.currentTarget.style.backgroundColor = "#3b82f6";
                      }}
                    >
                      <span></span>
                      <span>과제 파일</span>
                    </button>
                  </div>

                  {/* 과제 정보 */}
                  <div style={{
                    display: "flex",
                    gap: "16px",
                    fontSize: "13px",
                    color: "#64748b",
                    marginBottom: "16px",
                    paddingBottom: "16px",
                    borderBottom: "1px solid #e0f2fe"
                  }}>
                    <span>{file.file_name}</span>
                    <span> {formatFileSize(file.file_size)}</span>
                    <span>{new Date(file.created_at).toLocaleDateString('ko-KR')}</span>
                  </div>

                  {/* 학생 제출 상황 */}
                  <div style={{
                    backgroundColor: "white",
                    border: "1px solid #e0f2fe",
                    borderRadius: "12px",
                    padding: "20px"
                  }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "12px" }}>
                      <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                        <span style={{ fontWeight: "600", fontSize: "15px", color: "#334155" }}>학생 제출 상황</span>
                        {file.student_submission ? (
                          <span style={{
                            padding: "4px 12px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: "600",
                            backgroundColor: "#d1fae5",
                            color: "#059669"
                          }}>
                             제출완료
                          </span>
                        ) : (
                          <span style={{
                            padding: "4px 12px",
                            borderRadius: "20px",
                            fontSize: "12px",
                            fontWeight: "600",
                            backgroundColor: "#fee2e2",
                            color: "#dc2626"
                          }}>
                            미제출
                          </span>
                        )}
                      </div>

                      {file.student_submission && (
                        <button
                          onClick={() => toggleSubmissionExpansion(file.student_submission.id)}
                          style={{
                            padding: "6px 14px",
                            backgroundColor: expandedSubmissions.has(file.student_submission.id) ? "#e0f2fe" : "#3b82f6",
                            color: expandedSubmissions.has(file.student_submission.id) ? "#0284c7" : "white",
                            border: "none",
                            borderRadius: "8px",
                            cursor: "pointer",
                            fontSize: "13px",
                            fontWeight: "600",
                            transition: "all 0.2s"
                          }}
                          onMouseOver={(e) => {
                            if (!expandedSubmissions.has(file.student_submission.id)) {
                              e.currentTarget.style.backgroundColor = "#2563eb";
                            }
                          }}
                          onMouseOut={(e) => {
                            if (!expandedSubmissions.has(file.student_submission.id)) {
                              e.currentTarget.style.backgroundColor = "#3b82f6";
                            }
                          }}
                        >
                          {expandedSubmissions.has(file.student_submission.id) ? '접기' : '채점하기'}
                        </button>
                      )}
                    </div>

                    {file.student_submission && (
                      <div>
                        {/* 기본 제출 정보 */}
                        <div style={{
                          marginBottom: "12px",
                          paddingBottom: "12px",
                          borderBottom: "1px solid #f1f5f9"
                        }}>
                          {file.student_submission.submission_files && file.student_submission.submission_files.length > 0 ? (
                            <div>
                              <p style={{
                                fontWeight: "600",
                                fontSize: "14px",
                                color: "#334155",
                                marginBottom: "8px"
                              }}>
                                제출 파일 ({file.student_submission.submission_files.length}개)
                              </p>
                              {file.student_submission.submission_files.map((submissionFile, fileIndex) => (
                                <div key={fileIndex} style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                  marginBottom: "6px",
                                  padding: "6px 8px",
                                  backgroundColor: "#f8fafc",
                                  borderRadius: "4px"
                                }}>
                                  <span style={{ flex: 1, fontSize: "13px", color: "#475569" }}>
                                    {fileIndex + 1}. {submissionFile.name}
                                  </span>
                                  <button
                                    onClick={() => handleDownloadSubmission(submissionFile.s3_key, submissionFile.name)}
                                    style={{
                                      padding: "3px 8px",
                                      backgroundColor: "#10b981",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "4px",
                                      cursor: "pointer",
                                      fontSize: "11px",
                                      fontWeight: "600",
                                      transition: "all 0.2s"
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#059669"}
                                    onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#10b981"}
                                  >
                                    다운로드
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                              fontSize: "14px",
                              color: "#475569"
                            }}>
                              <span style={{ fontWeight: "500" }}>{file.student_submission.submission_file_name}</span>
                              <button
                                onClick={() => handleDownloadSubmission(file.student_submission.submission_s3_key, file.student_submission.submission_file_name)}
                                style={{
                                  padding: "4px 10px",
                                  backgroundColor: "#10b981",
                                  color: "white",
                                  border: "none",
                                  borderRadius: "6px",
                                  cursor: "pointer",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  transition: "all 0.2s"
                                }}
                                onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#059669"}
                                onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#10b981"}
                              >
                                다운로드
                              </button>
                            </div>
                          )}
                        </div>

                        {/* 현재 채점 상태 */}
                        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
                          {file.student_submission.score !== null ? (
                            <>
                              <span style={{
                                padding: "6px 14px",
                                borderRadius: "20px",
                                fontSize: "14px",
                                fontWeight: "600",
                                backgroundColor: file.student_submission.score >= 80 ? "#d1fae5" :
                                               file.student_submission.score >= 60 ? "#fef3c7" : "#fee2e2",
                                color: file.student_submission.score >= 80 ? "#059669" :
                                      file.student_submission.score >= 60 ? "#d97706" : "#dc2626"
                              }}>
                                 {formatScore(file.student_submission.score)}점
                              </span>
                              {file.student_submission.feedback_file_name && (
                                <button
                                  onClick={() => handleDownloadFeedback(file.student_submission.feedback_s3_key, file.student_submission.feedback_file_name)}
                                  style={{
                                    padding: "6px 12px",
                                    borderRadius: "20px",
                                    fontSize: "13px",
                                    fontWeight: "600",
                                    backgroundColor: "#dbeafe",
                                    color: "#0284c7",
                                    border: "none",
                                    cursor: "pointer",
                                    transition: "all 0.2s"
                                  }}
                                  onMouseOver={(e) => e.currentTarget.style.backgroundColor = "#bfdbfe"}
                                  onMouseOut={(e) => e.currentTarget.style.backgroundColor = "#dbeafe"}
                                  title="첨삭 파일 다운로드"
                                >
                                   첨삭파일
                                </button>
                              )}
                            </>
                          ) : (
                            <span style={{
                              padding: "6px 14px",
                              borderRadius: "20px",
                              fontSize: "13px",
                              fontWeight: "600",
                              backgroundColor: "#fef3c7",
                              color: "#d97706"
                            }}>
                              ⏳ 미채점
                            </span>
                          )}
                        </div>

                        {/* 채점 패널 (확장 시 표시) */}
                        {expandedSubmissions.has(file.student_submission.id) && (
                          <div style={{
                            marginTop: "16px",
                            padding: "20px",
                            backgroundColor: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            borderRadius: "12px"
                          }}>
                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              marginBottom: "16px"
                            }}>
                              <span style={{ fontSize: "18px" }}></span>
                              <h5 style={{ margin: "0", color: "#1e293b", fontSize: "16px", fontWeight: "600" }}>
                                채점하기
                              </h5>
                            </div>

                            <div style={{ marginBottom: "16px" }}>
                              <label style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "12px",
                                fontSize: "14px",
                                fontWeight: "500",
                                color: "#334155"
                              }}>
                                <span>점수 (0-100):</span>
                                <input
                                  type="number"
                                  min="0"
                                  max="100"
                                  step="0.01"
                                  value={gradingData[file.student_submission.id]?.score ?? file.student_submission.score ?? ''}
                                  onChange={(e) => updateGradingData(file.student_submission.id, 'score', e.target.value)}
                                  style={{
                                    padding: "8px 12px",
                                    border: "1px solid #cbd5e1",
                                    borderRadius: "8px",
                                    width: "100px",
                                    fontSize: "14px",
                                    fontWeight: "600"
                                  }}
                                />
                              </label>
                            </div>

                            <div style={{ marginBottom: "16px" }}>
                              <label style={{
                                fontSize: "14px",
                                fontWeight: "500",
                                display: "block",
                                marginBottom: "8px",
                                color: "#334155"
                              }}>
                                첨삭 파일 (선택사항):
                              </label>
                              
                              {/* 기존 첨삭 파일이 있는 경우 */}
                              {file.student_submission.feedback_file_name && !gradingData[file.student_submission.id]?.deleteFeedback && (
                                <div style={{
                                  marginBottom: "12px",
                                  padding: "12px",
                                  backgroundColor: "#dbeafe",
                                  borderRadius: "8px",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center"
                                }}>
                                  <div style={{ fontSize: "13px", color: "#0369a1", fontWeight: "500" }}>
                                     {file.student_submission.feedback_file_name}
                                  </div>
                                  <button
                                    onClick={() => clearFeedbackFile(file.student_submission.id)}
                                    style={{
                                      padding: "4px 10px",
                                      fontSize: "12px",
                                      backgroundColor: "#ef4444",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "6px",
                                      cursor: "pointer",
                                      fontWeight: "600"
                                    }}
                                  >
                                    삭제
                                  </button>
                                </div>
                              )}

                              {/* 첨삭 파일 삭제 표시 */}
                              {gradingData[file.student_submission.id]?.deleteFeedback && (
                                <div style={{
                                  marginBottom: "12px",
                                  padding: "12px",
                                  backgroundColor: "#fee2e2",
                                  borderRadius: "8px",
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center"
                                }}>
                                  <div style={{ fontSize: "13px", color: "#dc2626", fontWeight: "500" }}>
                                    첨삭 파일이 삭제됩니다.
                                  </div>
                                  <button
                                    onClick={() => updateGradingData(file.student_submission.id, 'deleteFeedback', false)}
                                    style={{
                                      padding: "4px 10px",
                                      fontSize: "12px",
                                      backgroundColor: "#10b981",
                                      color: "white",
                                      border: "none",
                                      borderRadius: "6px",
                                      cursor: "pointer",
                                      fontWeight: "600"
                                    }}
                                  >
                                    취소
                                  </button>
                                </div>
                              )}

                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.docx,.xlsx,.pptx,.txt,.md,.zip,.rar,.hwp,.hwpx"
                                onChange={(e) => updateGradingData(file.student_submission.id, 'feedbackFile', e.target.files[0])}
                                style={{
                                  fontSize: "13px",
                                  padding: "8px",
                                  border: "1px solid #cbd5e1",
                                  borderRadius: "8px",
                                  width: "100%",
                                  cursor: "pointer"
                                }}
                              />
                              {gradingData[file.student_submission.id]?.feedbackFile && (
                                <div style={{
                                  marginTop: "8px",
                                  fontSize: "13px",
                                  color: "#10b981",
                                  fontWeight: "500"
                                }}>
                                   {gradingData[file.student_submission.id].feedbackFile.name}
                                </div>
                              )}
                            </div>

                            <button
                              onClick={() => handleSubmitGrade(file.student_submission.id)}
                              disabled={uploadingFeedback.has(file.student_submission.id)}
                              style={{
                                padding: "10px 20px",
                                fontSize: "14px",
                                fontWeight: "600",
                                borderRadius: "8px",
                                backgroundColor: uploadingFeedback.has(file.student_submission.id) ? "#94a3b8" : "#10b981",
                                border: "none",
                                color: "white",
                                cursor: uploadingFeedback.has(file.student_submission.id) ? "not-allowed" : "pointer",
                                transition: "all 0.2s",
                                width: "100%"
                              }}
                              onMouseOver={(e) => {
                                if (!uploadingFeedback.has(file.student_submission.id)) {
                                  e.currentTarget.style.backgroundColor = "#059669";
                                }
                              }}
                              onMouseOut={(e) => {
                                if (!uploadingFeedback.has(file.student_submission.id)) {
                                  e.currentTarget.style.backgroundColor = "#10b981";
                                }
                              }}
                            >
                              {uploadingFeedback.has(file.student_submission.id) ? '⏳ 처리 중...' : '채점 완료'}
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HomeworkFilesModal;
